import type { FileRecord } from "@cohesive/api-client";
import { fileRecordTextQueryKey } from "@cohesive/ui-components";

export type RecoverableScope = "workspace" | "canvas";

/** Lists re-read on this cadence while visible; the re-read is what renews the links in the rows. */
export const FILE_RECORD_LIST_REFETCH_INTERVAL_MS = 20 * 60_000;

export const FILE_RECORD_LIST_OPTIONS = {
  refetchInterval: FILE_RECORD_LIST_REFETCH_INTERVAL_MS,
} as const;

/** The list a file row is read from: its class and the container's id. */
export type FileRecordListScope =
  | { class: "workspace"; workspace_id: string }
  | { class: "canvas"; canvas_id: string }
  | { class: "flow-session"; flow_session_id: string };

export const fileRecordKeys = {
  all: ["fileRecords"] as const,
  recent: (limit?: number) =>
    limit === undefined
      ? (["fileRecords", "recent"] as const)
      : (["fileRecords", "recent", limit] as const),
  /** Every workspace tree — the prefix for a workspace-agnostic invalidation. */
  workspaces: ["fileRecords", "workspace"] as const,
  workspace: (workspaceId: string) =>
    ["fileRecords", "workspace", workspaceId] as const,
  canvas: (canvasId: string) => ["fileRecords", "canvas", canvasId] as const,
  sharedWithMe: () => ["fileRecords", "sharedWithMe"] as const,
  flowSession: (flowSessionId: string) =>
    ["fileRecords", "flowSession", flowSessionId] as const,
  recentlyDeleted: (scope: RecoverableScope, scopeId: string) =>
    ["fileRecords", "recentlyDeleted", scope, scopeId] as const,
  /** A row with no list around it (deep link, search hit): `GET file-records/:id`. */
  detail: (id: string) => ["fileRecord", id] as const,
  tiptapToken: (id: string) =>
    [...fileRecordKeys.detail(id), "tiptapToken"] as const,
  collaborationToken: (id: string) =>
    [...fileRecordKeys.detail(id), "collaborationToken"] as const,
  versions: (id: string) => [...fileRecordKeys.detail(id), "versions"] as const,
  /** Body text by file version — see `useFileRecordText` (ui-components).
   *  Deliberately outside the `fileRecords` prefix. */
  text: (versionKey: string) => fileRecordTextQueryKey(versionKey),
};

/** `["fileRecord", id]` itself, not the token and version entries under it. */
export function isFileRecordRowKey(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === "fileRecord" && queryKey.length === 2;
}

const SCOPE_LIST_SEGMENTS = new Set(["workspace", "canvas", "flowSession"]);

/** A list that holds exactly the rows of one container. */
export function isFileRecordScopeListKey(
  queryKey: readonly unknown[],
): boolean {
  return (
    queryKey[0] === "fileRecords" &&
    queryKey.length === 3 &&
    SCOPE_LIST_SEGMENTS.has(queryKey[1] as string)
  );
}

export function fileRecordListKey(scope: FileRecordListScope) {
  switch (scope.class) {
    case "workspace":
      return fileRecordKeys.workspace(scope.workspace_id);
    case "canvas":
      return fileRecordKeys.canvas(scope.canvas_id);
    case "flow-session":
      return fileRecordKeys.flowSession(scope.flow_session_id);
  }
}

/** The scope a row lives in; undefined when its fields do not name one. */
export function fileRecordListScope(
  record:
    | Partial<
        Pick<
          FileRecord,
          "class" | "workspace_id" | "canvas_id" | "flow_session_id"
        >
      >
    | null
    | undefined,
): FileRecordListScope | undefined {
  if (!record) return undefined;
  if (record.class === "workspace" && record.workspace_id) {
    return { class: "workspace", workspace_id: record.workspace_id };
  }
  if (record.class === "canvas" && record.canvas_id) {
    return { class: "canvas", canvas_id: record.canvas_id };
  }
  if (record.class === "flow-session" && record.flow_session_id) {
    return { class: "flow-session", flow_session_id: record.flow_session_id };
  }
  return undefined;
}
